// =====================================================
// Password Validation Utility
// AdminMunicipal v6.0 - Security Enhancement
// Enforces strong password requirements
// =====================================================

export interface PasswordValidationResult {
  valid: boolean;
  message: string;
  strength: 'weak' | 'medium' | 'strong';
  requirements: {
    minLength: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasNumber: boolean;
    hasSpecial: boolean;
  };
}

/**
 * Validates password strength according to security best practices.
 * Requirements:
 * - Minimum 8 characters (NIST recommendation)
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character (recommended but not required for 'medium')
 *
 * @param password - The password to validate
 * @returns Validation result with strength indicator and specific requirements status
 */
export function validatePassword(password: string): PasswordValidationResult {
  const requirements = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>\-_=+\[\]\\;'`~]/.test(password),
  };

  // Check minimum length first
  if (!requirements.minLength) {
    return {
      valid: false,
      message: 'La contraseña debe tener al menos 8 caracteres',
      strength: 'weak',
      requirements,
    };
  }

  // Check for uppercase
  if (!requirements.hasUppercase) {
    return {
      valid: false,
      message: 'La contraseña debe contener al menos una letra mayúscula',
      strength: 'weak',
      requirements,
    };
  }

  // Check for lowercase
  if (!requirements.hasLowercase) {
    return {
      valid: false,
      message: 'La contraseña debe contener al menos una letra minúscula',
      strength: 'weak',
      requirements,
    };
  }

  // Check for number
  if (!requirements.hasNumber) {
    return {
      valid: false,
      message: 'La contraseña debe contener al menos un número',
      strength: 'weak',
      requirements,
    };
  }

  // Check for special character (recommended but not blocking)
  if (!requirements.hasSpecial) {
    return {
      valid: true, // Still valid, but medium strength
      message: 'Contraseña aceptable. Añade un carácter especial (!@#$%^&*) para mayor seguridad',
      strength: 'medium',
      requirements,
    };
  }

  // All requirements met
  return {
    valid: true,
    message: 'Contraseña segura',
    strength: 'strong',
    requirements,
  };
}

/**
 * Returns the CSS class for password strength indicator
 */
export function getStrengthColor(strength: 'weak' | 'medium' | 'strong'): string {
  switch (strength) {
    case 'weak':
      return 'text-red-500';
    case 'medium':
      return 'text-yellow-500';
    case 'strong':
      return 'text-green-500';
  }
}

/**
 * Returns the background CSS class for password strength bar
 */
export function getStrengthBarColor(strength: 'weak' | 'medium' | 'strong'): string {
  switch (strength) {
    case 'weak':
      return 'bg-red-500';
    case 'medium':
      return 'bg-yellow-500';
    case 'strong':
      return 'bg-green-500';
  }
}

/**
 * Returns the width percentage for password strength bar
 */
export function getStrengthBarWidth(strength: 'weak' | 'medium' | 'strong'): string {
  switch (strength) {
    case 'weak':
      return 'w-1/3';
    case 'medium':
      return 'w-2/3';
    case 'strong':
      return 'w-full';
  }
}
